import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Layout, Page, Card, Button, Banner, Link, Popover, ActionList, BlockStack, Box, InlineStack, Text, Modal, Tabs } from "@shopify/polaris";
import { PrintIcon } from "@shopify/polaris-icons";
import { getRequest } from "../../shared/components/functions";
import { printAllPartLabels } from "../../shared/dymo/labels/parts_labels/partLabel";
import TimelineBox from "./components/timelineBox";
import ShippingInfoBox from "./components/shippingInfoBox";
import SummaryBox from "./components/summaryBox";
import DocsBox from "./components/docsBox";
import SerialsBox from "./components/serialsBox";
import { FulFillInput } from "./components/fullfilInput";
import { PalletLabelPrintModal } from "./components/palletLabelPrintModal";
import { OrderLineLabelModal } from "./components/orderLineLabelModal";
import { CardHeading } from "../../shared/components/CardHeading";
import { PageSpinner } from "../../shared/components/spinner/PageSpinner";
import FulfilmentStepsProgress from "./components/fulfilmentStepsProgress";
import OrderPackagingBox from "./components/orderPackagingBox";
import { ImageThumbNailViewer, getPrimaryS3ImageKey } from "../../shared/components/s3_image_display/ImageThumbNailViewer";
import { Toasts } from "../../shared/components/toasts/toasts";

/*
  Stages of fulilling an order:
  x all lines need to be fulfilled
  x translated lines
  x add stock to sale
  - stock need to be adjusted also.. for aha
  - fulfilment records still need to be written? Maybe  not! Only benefit is to know what is when fulfilled by who
  - all serials need to be connected and maybe scanned?
*/

const saleLinesTableStyles = {
  wrapper: {
    overflow: "auto",
    position: "relative",
    maxHeight: "calc(100dvh - 160px)",
  },
  table: {
    width: "100%",
    minWidth: "1180px",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  headingCell: {
    padding: "12px 16px",
    borderBottom: "1px solid #dfe3e8",
    background: "#f6f6f7",
    color: "#6d7175",
    fontSize: "0.75rem",
    fontWeight: 600,
    textAlign: "left",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 10,
    boxShadow: "inset 0 -1px 0 #dfe3e8",
  },
  cell: {
    padding: "12px 16px",
    borderBottom: "1px solid #dfe3e8",
    verticalAlign: "middle",
    position: "relative",
    zIndex: 0,
  },
  codeCell: {
    fontSize: "1rem",
    fontWeight: 600,
  },
  fulfilledRow: {
    background: "#e8f7eb",
  },
  headingButton: {
    appearance: "none",
    background: "transparent",
    border: 0,
    color: "inherit",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    font: "inherit",
    gap: "2px",
    padding: 0,
    textAlign: "left",
    textTransform: "inherit",
    width: "100%",
  },
  headingButtonMeta: {
    fontSize: "0.7rem",
    textTransform: "none",
  },
};

const normalizeLocationSortValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const compareLocationValue = (firstValue, secondValue, direction) => {
  if (firstValue === null && secondValue === null) return 0;
  if (firstValue === null) return 1;
  if (secondValue === null) return -1;
  return direction === "ascending" ? firstValue - secondValue : secondValue - firstValue;
};

export default function SalePage() {
  let { id } = useParams();

  const order_schema = {
    order: [],
    lines: [],
    lines_translated: [],
    serials: [],
    packages: [],
  };

  const [initialSale, setInitialSale] = useState(order_schema);
  const [sale, setSale] = useState(order_schema);
  const [loaded, setLoaded] = useState(false);
  const [fulfillingOrder, setFulfillingOrder] = useState(false);
  const [toast, setToast] = useState(null);
  const [bigLabelActive, setBigLabelActive] = useState(false);
  const [smallLabelActive, setSmallLabelActive] = useState(false);
  const [externalPayloadModalOpen, setExternalPayloadModalOpen] = useState(false);
  const [palletLabelModalOpen, setPalletLabelModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [locationSortDirection, setLocationSortDirection] = useState("ascending");
  const [hoveredSaleLineKey, setHoveredSaleLineKey] = useState(null);
  const shippingInfoBoxRef = useRef(null);
  const orderPackagingBoxRef = useRef(null);
  let orderCanBeFulfilled = true;
  let orderHasTranslationErrors = false;

  useEffect(() => {
    getSale();
  }, []);

  // useEffect(() => {
  //   //if(orderHasTranslationErrors) setOrderCanBeFulfilled(false)
  // }, [orderHasTranslationErrors]);

  async function getSale() {
    const data = await getRequest(`/api/sales/${id}`);
    setSale(data);
    setInitialSale(data);
    setLoaded(true);
    return data;
  }

  let qtyMachines = sale?.lines.reduce((acc, line) => {
    if (line.produktnr === 10956 || line.produktnr === 10957) acc += line.aantal;
    return acc;
  }, 0);

  const allSerialsAdded = qtyMachines <= sale?.serials.length;

  const saleLineRows = Array.isArray(sale.lines)
    ? sale.lines
        .map((line, originalIndex) => {
        const productAha = sale.lines_translated.find((tl) => tl.OnderdeelSchuilenburgID === line.produktnr);
        const remaingToFulfill = line.aantal - (line.aantalFulfilled || 0);
        const productAhaId = productAha?.OnderdeelAhaID;
        const productAhaName = productAha?.ProductNameNL;
        const productSupplierReference = productAha?.Productref ? String(productAha.Productref).trim() : "";
        const ahaRatio = productAha?.ratio_aha || 1;
        const schuilRatio = productAha?.ratio_schuilenburg || 1;
        const ahaQty = (line.aantal / ahaRatio) * schuilRatio;
        const schuilRef = Number(line.produktnr);
        const isFulfilled = remaingToFulfill === 0;
        const hasTranslationError = !productAhaId;
        const productImageS3Key = getPrimaryS3ImageKey(productAha?.Image);
        const rack = normalizeLocationSortValue(productAha?.Rack);
        const shelf = normalizeLocationSortValue(productAha?.Legplank);
        const locationName = productAha?.warehouse_pickingplace || (productAha?.Stockplaats === 1 ? "Magazijn PM" : "");
        const hasLocation = locationName || rack !== null || shelf !== null;

        if (hasTranslationError) {
          orderHasTranslationErrors = true;
        }

        return {
          key: line.OrderbevestigingLijnID,
          line,
          isFulfilled,
          productImage: <ImageThumbNailViewer s3Key={productImageS3Key} key={`sale_line_image_${line.OrderbevestigingLijnID}`} />,
          productLink: hasTranslationError ? (
            <Text as="span" tone="critical">
              Missing
            </Text>
          ) : (
            <Link url={`/products/${productAhaId}`}>{productAhaId}</Link>
          ),
          description: (
            <BlockStack gap="100">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "baseline" }}>
                <Text as="span" fontWeight="semibold" tone={!productAhaName ? "critical" : undefined}>
                  {`${productAhaName || "TRANSLATION ERROR NO AHA PIECE FOUND FOR SCHUIL PIECE"}`}
                </Text>
                {productSupplierReference ? (
                  <Text as="span" tone="subdued">
                    <span style={{ fontStyle: "italic" }}>{`- (supplier ref: ${productSupplierReference})`}</span>
                  </Text>
                ) : null}
              </div>
              <Text>{`schuil: #${line.produktnr} ${line.tekstlijn} (x${line.aantal})`}</Text>
            </BlockStack>
          ),
          originalIndex,
          schuilRef,
          rack,
          shelf,
          ahaQty,
          location: hasTranslationError ? (
            <Text as="span" tone="critical">
              Translation required
            </Text>
          ) : hasLocation ? (
            <BlockStack gap="100">
              <Text as="span" fontWeight="semibold">
                {locationName || "Magazijn PM"}
              </Text>
              <Text as="span" tone="subdued">
                {`Rack ${rack ?? "-"} · Shelf ${shelf ?? "-"}`}
              </Text>
            </BlockStack>
          ) : (
            <Text as="span" tone="subdued">
              No standard rack / shelf
            </Text>
          ),
          remainingToFulfill: (
            <Text as="span" fontWeight="semibold" tone={remaingToFulfill > 0 ? "critical" : "success"}>
              {remaingToFulfill}
            </Text>
          ),
          stock: (
            <Text as="span" fontWeight="semibold" tone={Number(productAha?.aantal || 0) < ahaQty ? "critical" : "success"}>
              {productAha?.aantal ?? "-"}
            </Text>
          ),
          fulfilledQty: line.aantalFulfilled || 0,
          fulfilAction: hasTranslationError ? (
            <Text as="span" tone="critical">
              Translation required
            </Text>
          ) : (
            <FulFillInput line={line} getSale={getSale} ahaInventory={productAha.aantal} ahaProductId={productAhaId} remaingToFulfill={remaingToFulfill} />
          ),
          status: isFulfilled ? "✅" : "❌",
          labelAction: hasTranslationError ? null : <OrderLineLabelModal line={line} />,
        };
      })
        .sort((a, b) => {
          const isBottomRefA = Number.isFinite(a.schuilRef) && a.schuilRef < 50;
          const isBottomRefB = Number.isFinite(b.schuilRef) && b.schuilRef < 50;

          if (isBottomRefA !== isBottomRefB) {
            return isBottomRefA ? 1 : -1;
          }

          const rackCompare = compareLocationValue(a.rack, b.rack, locationSortDirection);
          if (rackCompare !== 0) return rackCompare;

          const shelfCompare = compareLocationValue(a.shelf, b.shelf, locationSortDirection);
          if (shelfCompare !== 0) return shelfCompare;

          return a.originalIndex - b.originalIndex;
        })
    : [];

  const sortedPrintableLines = saleLineRows.map((row) => row.line || sale.lines[row.originalIndex]).filter(Boolean);

  // an order is fulfilled when for all the lines the qty is fulfilled
  const saleIsFulfilled = () => {
    let fulfilled = true;
    sale.lines.forEach((line) => {
      if (line.aantalFulfilled < line.aantal && line.produktnr > 50) fulfilled = false;
    });
    return fulfilled;
  };

  const saleIsFullyFulfilled = saleIsFulfilled();

  const handleStepFiveAction = async () => {
    await shippingInfoBoxRef.current?.runPrimaryAction?.();
  };

  const handlePackagingChanged = async () => {
    await getSale();
    await shippingInfoBoxRef.current?.refresh?.();
  };

  orderCanBeFulfilled = !orderHasTranslationErrors;

  const toggleLocationSortDirection = () => {
    setLocationSortDirection((currentDirection) => (currentDirection === "ascending" ? "descending" : "ascending"));
  };

  const tabs = [
    {
      id: 'formatted',
      content: 'Order Details',
      accessibilityLabel: 'Order Details',
      panelID: 'formatted-content',
    },
    {
      id: 'raw',
      content: 'Raw Payload',
      accessibilityLabel: 'Raw Payload',
      panelID: 'raw-content',
    },
  ];

  const renderFormattedPayload = (payload) => {
    try {
      const data = JSON.parse(payload);
      return (
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Order Information</Text>
              <InlineStack gap="400" wrap={false}>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="span" fontWeight="semibold">Order Number:</Text>
                  <Text variant="bodyMd" as="span">{data?.order?.increment_id || 'N/A'}</Text>
                </BlockStack>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="span" fontWeight="semibold">Order Date:</Text>
                  <Text variant="bodyMd" as="span">{data?.order?.created_at ? new Date(data.order.created_at).toLocaleDateString() : 'N/A'}</Text>
                </BlockStack>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="span" fontWeight="semibold">Total Amount:</Text>
                  <Text variant="bodyMd" as="span">€{data?.order?.grand_total?.toFixed(2) || '0.00'}</Text>
                </BlockStack>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="span" fontWeight="semibold">Shipping Cost:</Text>
                  <Text variant="bodyMd" as="span">€{data?.order?.shipping_amount?.toFixed(2) || '0.00'}</Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Shipping Information</Text>
              <BlockStack gap="200">
                <Text variant="bodyMd" as="span" fontWeight="semibold">Shipping Method:</Text>
                <Text variant="bodyMd" as="span">{data?.shipping?.[0]?.name || 'N/A'}</Text>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Shipping Status:</Text>
                <Text variant="bodyMd" as="span">{data?.shipping?.[0]?.state || 'N/A'}</Text>
              </BlockStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Customer Information</Text>
              <BlockStack gap="200">
                <Text variant="bodyMd" as="span" fontWeight="semibold">Name:</Text>
                <Text variant="bodyMd" as="span">{data?.customer ? `${data.customer.firstName || ''} ${data.customer.lastName || ''}`.trim() || 'N/A' : 'N/A'}</Text>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Company:</Text>
                <Text variant="bodyMd" as="span">{data?.customer?.company || 'N/A'}</Text>
                <Text variant="bodyMd" as="span" fontWeight="semibold">Email:</Text>
                <Text variant="bodyMd" as="span">{data?.customer?.email || 'N/A'}</Text>
                <Text variant="bodyMd" as="span" fontWeight="semibold">VAT ID:</Text>
                <Text variant="bodyMd" as="span">{data?.customer?.vatIds?.[0] || 'N/A'}</Text>
              </BlockStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Shipping Address</Text>
              <BlockStack gap="200">
                {data?.order?.shipping_address && (
                  <>
                    <Text variant="bodyMd" as="span">{data.order.shipping_address.company || 'N/A'}</Text>
                    {data.order.shipping_address.department && (
                      <Text variant="bodyMd" as="span">{data.order.shipping_address.department}</Text>
                    )}
                    <Text variant="bodyMd" as="span">
                      {[
                        data.order.shipping_address.street,
                        data.order.shipping_address.address1
                      ].filter(Boolean).join(' ') || 'N/A'}
                    </Text>
                    <Text variant="bodyMd" as="span">
                      {[
                        data.order.shipping_address.zipcode,
                        data.order.shipping_address.city
                      ].filter(Boolean).join(' ') || 'N/A'}
                    </Text>
                    <Text variant="bodyMd" as="span">{data.order.shipping_address.country || 'N/A'}</Text>
                  </>
                )}
              </BlockStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Billing Address</Text>
              <BlockStack gap="200">
                {data?.order?.billing_address && (
                  <>
                    <Text variant="bodyMd" as="span">{data.order.billing_address.company || 'N/A'}</Text>
                    {data.order.billing_address.department && (
                      <Text variant="bodyMd" as="span">{data.order.billing_address.department}</Text>
                    )}
                    <Text variant="bodyMd" as="span">
                      {[
                        data.order.billing_address.street,
                        data.order.billing_address.address1
                      ].filter(Boolean).join(' ') || 'N/A'}
                    </Text>
                    <Text variant="bodyMd" as="span">
                      {[
                        data.order.billing_address.zipcode,
                        data.order.billing_address.city
                      ].filter(Boolean).join(' ') || 'N/A'}
                    </Text>
                    <Text variant="bodyMd" as="span">{data.order.billing_address.country || 'N/A'}</Text>
                  </>
                )}
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      );
    } catch (error) {
      return (
        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">Error</Text>
            <Text variant="bodyMd">Failed to parse payload data. Please check the raw payload tab for details.</Text>
          </BlockStack>
        </Card>
      );
    }
  };

  if (!loaded) return <PageSpinner />;

  return (
    <Page 
      backAction={{ content: "Sales", url: "/fulfilment" }} 
      title={`Sales order #${sale.order.order_number}`} 
      fullWidth
      secondaryActions={[
        {
          content: "Print pallet label",
          icon: PrintIcon,
          onAction: () => setPalletLabelModalOpen(true),
        },
      ]}
      primaryAction={
        sale.order.ExternalPayload ? {
          content: 'View Webshop Information',
          onAction: () => setExternalPayloadModalOpen(true)
        } : undefined
      }
    >
      <BlockStack gap="400">
        {orderHasTranslationErrors && (
          <Banner tone="critical" title="This order has translation errors, it cannot be fulfilled! Or the AHA part does not have a valid stockplaats for the Magazijn!!" />
        )}

        {loaded && (
          <BlockStack>
            {!saleIsFullyFulfilled ? (
              <Banner tone={orderCanBeFulfilled ? "warning" : "critical"} title="This order is not fulfilled yet.">
              </Banner>
            ) : (
              <Banner tone="success" title="This order is fulfilled.">
              </Banner>
            )}

            {!allSerialsAdded && <Banner tone="critical" title="You need to add all serial numbers!" />}
          </BlockStack>
        )}

        <FulfilmentStepsProgress
          sale={sale}
          stepThreeAction={{ label: "Manage packaging", onAction: () => orderPackagingBoxRef.current?.scrollIntoView?.() }}
          stepFiveAction={{
            label: sale.order.sendcloud_id ? "Print label" : saleIsFullyFulfilled ? "Review & send" : "Get quote",
            onAction: handleStepFiveAction,
            disabled: false,
            loading: false,
          }}
        />
        <OrderPackagingBox ref={orderPackagingBoxRef} orderId={sale.order.order_id} packages={sale.packages} onChanged={handlePackagingChanged} />
        <Toasts toast={toast} setToast={setToast} toastContent="Sendcloud label ready" errorToastContent="Sendcloud action failed" />

        {sale.order.ExternalPayload && (
          <Modal
            open={externalPayloadModalOpen}
            onClose={() => setExternalPayloadModalOpen(false)}
            title="Webshop Information"
            primaryAction={{
              content: 'Close',
              onAction: () => setExternalPayloadModalOpen(false),
            }}
          >
            <Modal.Section>
              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                {selectedTab === 0 ? (
                  renderFormattedPayload(sale.order.ExternalPayload)
                ) : (
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word',
                    backgroundColor: 'var(--p-surface-secondary)',
                    padding: '1rem',
                    borderRadius: 'var(--p-border-radius-200)',
                    fontSize: 'var(--p-font-size-200)',
                    lineHeight: '1.5'
                  }}>
                    {JSON.stringify(JSON.parse(sale.order.ExternalPayload), null, 2)}
                  </pre>
                )}
              </Tabs>
            </Modal.Section>
          </Modal>
        )}

        <PalletLabelPrintModal open={palletLabelModalOpen} onClose={() => setPalletLabelModalOpen(false)} order={sale.order} />

        {sale.order.note && (
          <Card>
            <CardHeading title="Note" />
            {sale.order.note}
          </Card>
        )}

        <Card>
          <Box paddingBlockEnd="300">
            <InlineStack align="space-between" wrap={false}>
              <Text variant="headingMd" as="h2">
                Lines
              </Text>
              <InlineStack gap="300">
                <Popover
                  active={smallLabelActive}
                  activator={
                    <Button disclosure onClick={() => setSmallLabelActive(true)}>
                      Print small labels
                    </Button>
                  }
                  onClose={() => setSmallLabelActive(false)}>
                  <ActionList
                    items={[
                      { content: "With qty (one label per line)", onAction: () => printAllPartLabels(sortedPrintableLines, true, true) },
                      { content: "Without qty (one label per individual item)", onAction: () => printAllPartLabels(sortedPrintableLines, false, true) },
                    ]}
                  />
                </Popover>
                <Popover
                  active={bigLabelActive}
                  activator={
                    <Button disclosure onClick={() => setBigLabelActive(true)}>
                      Print big labels
                    </Button>
                  }
                  onClose={() => setBigLabelActive(false)}>
                  <ActionList
                    items={[
                      { content: "With qty (one label per line)", onAction: () => printAllPartLabels(sortedPrintableLines, true, false) },
                      { content: "Without qty (one label per individual item)", onAction: () => printAllPartLabels(sortedPrintableLines, false, false) },
                    ]}
                  />
                </Popover>
              </InlineStack>
            </InlineStack>
          </Box>

          <div style={saleLinesTableStyles.wrapper}>
            <table style={saleLinesTableStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...saleLinesTableStyles.headingCell, width: "72px", textAlign: "center" }}>Img</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, width: "110px" }}>Code</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, minWidth: "320px" }}>Description</th>
                  <th style={saleLinesTableStyles.headingCell}>Qty</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, minWidth: "180px" }}>
                    <button
                      type="button"
                      onClick={toggleLocationSortDirection}
                      style={saleLinesTableStyles.headingButton}
                      aria-label={`Sort by rack and shelf ${locationSortDirection === "ascending" ? "descending" : "ascending"}`}>
                      <span>Standard location</span>
                      <span style={saleLinesTableStyles.headingButtonMeta}>{`Rack / shelf ${locationSortDirection === "ascending" ? "↑" : "↓"}`}</span>
                    </button>
                  </th>
                  <th style={saleLinesTableStyles.headingCell}>Remaining</th>
                  <th style={saleLinesTableStyles.headingCell}>Stock</th>
                  <th style={saleLinesTableStyles.headingCell}>Fulfilled</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, minWidth: "220px" }}>Fulfill</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, textAlign: "center", width: "90px" }}>Status</th>
                  <th style={{ ...saleLinesTableStyles.headingCell, width: "150px" }}>Labels</th>
                </tr>
              </thead>
              <tbody>
                {saleLineRows.map((row, index) => {
                  const isLastRow = index === saleLineRows.length - 1;
                  const cellStyle = isLastRow ? { ...saleLinesTableStyles.cell, borderBottom: "none" } : saleLinesTableStyles.cell;
                  const rowStyle =
                    hoveredSaleLineKey === row.key
                      ? { background: row.isFulfilled ? "#d6f0dc" : "#f1f2f3" }
                      : row.isFulfilled
                        ? saleLinesTableStyles.fulfilledRow
                        : undefined;

                  return (
                    <tr key={row.key} style={rowStyle} onMouseEnter={() => setHoveredSaleLineKey(row.key)} onMouseLeave={() => setHoveredSaleLineKey(null)}>
                      <td style={{ ...cellStyle, width: "72px", textAlign: "center" }}>{row.productImage}</td>
                      <td style={{ ...cellStyle, ...saleLinesTableStyles.codeCell, whiteSpace: "nowrap" }}>{row.productLink}</td>
                      <td style={cellStyle}>{row.description}</td>
                      <td style={cellStyle}>{row.ahaQty}</td>
                      <td style={cellStyle}>{row.location}</td>
                      <td style={cellStyle}>{row.remainingToFulfill}</td>
                      <td style={cellStyle}>{row.stock}</td>
                      <td style={cellStyle}>{row.fulfilledQty}</td>
                      <td style={cellStyle}>{row.fulfilAction}</td>
                      <td style={{ ...cellStyle, textAlign: "center", fontSize: "1.125rem" }}>{row.status}</td>
                      <td style={cellStyle}>{row.labelAction}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* <Card title="Translated lines" sectioned>
            Translated lines will come here
          </Card> */}

        <Layout>
          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <SummaryBox sale={sale} loaded={loaded} />
              <DocsBox sale={sale} loaded={loaded} />
              <TimelineBox timeline={sale.timeline} loaded={loaded} />
            </BlockStack>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <ShippingInfoBox ref={shippingInfoBoxRef} sale={sale} loaded={loaded} getSale={getSale} setToast={setToast} saleIsFullyFulfilled={saleIsFullyFulfilled} />
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <SerialsBox sale={sale} getSale={getSale} />
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}
